import { LEAGUES, resolveSupportedLeagueCode } from './leagues';

/** Clash slot result codes from API: h = home win, a = away win, t = tie. */

function competitionNameFromSeasonish(value) {
  const code = resolveSupportedLeagueCode(value);
  if (!code) return null;
  return LEAGUES.find((l) => l.code === code)?.name ?? null;
}

/**
 * Tier cell from league id `EL2026#2-2#2` or stored name `EL2026 pyramid 2-2@2`.
 * @returns {{ competitionName: string, cell: string }|null}
 */
function parseTierClashParts(leagueId, leagueName) {
  const id = String(leagueId ?? '').trim();
  if (id) {
    const hashIdx = id.indexOf('#');
    if (hashIdx > 0) {
      const seasonPart = id.slice(0, hashIdx);
      const rest = id.slice(hashIdx + 1);
      const lastHash = rest.lastIndexOf('#');
      if (lastHash > 0) {
        const cell = rest.slice(0, lastHash);
        if (/^\d+-\d+$/.test(cell)) {
          const competitionName = competitionNameFromSeasonish(seasonPart);
          if (competitionName) return { competitionName, cell };
        }
      }
    }
  }

  const name = String(leagueName ?? '').trim();
  const m = name.match(/^([A-Za-z0-9]+)\s+pyramid\s+(\d+-\d+)@\d+$/i);
  if (m) {
    const competitionName = competitionNameFromSeasonish(m[1]);
    if (competitionName) return { competitionName, cell: m[2] };
  }
  return null;
}

/**
 * User-facing clash league label for the clash sheet / chip titles.
 * Tier pyramid → "Eliteserien 2-2"; private leagues keep their name.
 * @returns {{ label: string|null, embedsCompetition: boolean }}
 */
export function formatClashLeagueDisplay(leagueName, leagueId) {
  const tier = parseTierClashParts(leagueId, leagueName);
  if (tier) {
    return { label: `${tier.competitionName} ${tier.cell}`, embedsCompetition: true };
  }
  const name =
    leagueName != null && String(leagueName).trim() !== '' ? String(leagueName).trim() : null;
  return { label: name, embedsCompetition: false };
}

export function isClashBye(awayUser) {
  return awayUser == null || awayUser === '';
}

/**
 * Outcome for one player in a clash slot.
 * @returns {'bye'|'win'|'loss'|'tie'|'pending'|null}
 */
export function clashOutcomeForPlayer(homeUser, awayUser, result, username, alreadyCalculated) {
  if (!username) return null;
  const u = String(username).toLowerCase();
  const h = homeUser != null ? String(homeUser).toLowerCase() : '';
  const a = awayUser != null && awayUser !== '' ? String(awayUser).toLowerCase() : '';
  const side = h === u ? 'h' : a === u ? 'a' : null;
  if (!side) return null;

  if (isClashBye(awayUser)) return 'bye';
  if (!alreadyCalculated || result == null) return 'pending';
  if (result === 't') return 'tie';
  if (result === side) return 'win';
  return 'loss';
}

export function formatClashOutcomeLabel(outcome) {
  switch (outcome) {
    case 'bye':
      return 'Bye';
    case 'win':
      return 'Win';
    case 'loss':
      return 'Loss';
    case 'tie':
      return 'Tie';
    case 'pending':
      return 'Pending';
    default:
      return '—';
  }
}

/**
 * Compact status for non-user rows (winner is indicated on the name).
 */
export function formatClashFixtureStatus(homeUser, awayUser, result, alreadyCalculated) {
  if (isClashBye(awayUser)) return 'Bye';
  if (!alreadyCalculated || result == null) return 'Pending';
  if (result === 't') return 'Tie';
  return null;
}

/** @returns {'h'|'a'|null} Winning side once the clash is resolved; null for ties or pending. */
export function clashWinnerSide(result, alreadyCalculated) {
  if (!alreadyCalculated || result == null) return null;
  if (result === 'h' || result === 'a') return result;
  return null;
}

function clashPointsAreValid(homePoints, awayPoints) {
  if (homePoints == null || awayPoints == null) return false;
  const hp = Number(homePoints);
  const ap = Number(awayPoints);
  return Number.isFinite(hp) && Number.isFinite(ap);
}

/** Whether a clash slot has round totals to display (including the default 0–0). */
export function clashHasScoreline(homePoints, awayPoints, alreadyCalculated) {
  return clashPointsAreValid(homePoints, awayPoints);
}

/** True when points are in play but the round clash is not finalized yet. */
export function clashScorelineIsLive(homePoints, awayPoints, alreadyCalculated) {
  if (!clashPointsAreValid(homePoints, awayPoints) || alreadyCalculated) return false;
  const hp = Number(homePoints);
  const ap = Number(awayPoints);
  return hp + ap > 0;
}

/**
 * Normalized scoreline values when {@link clashHasScoreline} is true.
 * @returns {{ homePoints: number, awayPoints: number, isLive: boolean, result: string|null }|null}
 */
export function formatClashScoreline(homePoints, awayPoints, result, alreadyCalculated) {
  if (!clashHasScoreline(homePoints, awayPoints, alreadyCalculated)) return null;
  const isLive = clashScorelineIsLive(homePoints, awayPoints, alreadyCalculated);
  return {
    homePoints: Number(homePoints),
    awayPoints: Number(awayPoints),
    isLive,
    result: isLive ? null : result
  };
}

export function clashSlotIncludesUser(homeUser, awayUser, username) {
  if (!username) return false;
  const u = String(username).toLowerCase();
  const h = homeUser != null ? String(homeUser).toLowerCase() : '';
  const a = awayUser != null && awayUser !== '' ? String(awayUser).toLowerCase() : '';
  return h === u || a === u;
}

function byePointsAreValid(homePoints) {
  if (homePoints == null) return false;
  const hp = Number(homePoints);
  return Number.isFinite(hp);
}

/** Whether a bye slot has match points to display (including 0 before the round starts). */
export function clashByeHasScoreline(homePoints) {
  return byePointsAreValid(homePoints);
}

/** True when bye match points are accumulating but the round is not finalized yet. */
export function clashByeScorelineIsLive(homePoints, alreadyCalculated) {
  if (!byePointsAreValid(homePoints) || alreadyCalculated) return false;
  return Number(homePoints) > 0;
}

/**
 * Normalized bye scoreline when {@link clashByeHasScoreline} is true.
 * @returns {{ homePoints: number, isLive: boolean }|null}
 */
export function formatClashByeScoreline(homePoints, alreadyCalculated) {
  if (!clashByeHasScoreline(homePoints)) return null;
  return {
    homePoints: Number(homePoints),
    isLive: clashByeScorelineIsLive(homePoints, alreadyCalculated)
  };
}
